import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { Modal } from '@myhealth-raccoon/components';

import { UpdateEmailForm } from 'src/components/forms/UpdateEmailForm';
import { authUserService } from 'src/services/authUser';
import { VerificationCodeForm, VerificationCodeFormValues } from 'src/components/forms/VerificationCodeForm';
import { captureException } from '@sentry/react';

type Props = {
    className?: string;
    currentEmail?: string;
    setIsDirty?(isDirty: boolean): void;
    afterSubmit?(): void;
}

export function UpdateEmail({ className, currentEmail, setIsDirty, afterSubmit }: Props) {
    const [isUpdateForm, setIsUpdateForm] = useState(!currentEmail);
    const [emailToVerify, setEmailToVerify] = useState<string>();
    useEffect(() => {
        // cannot continue without an existing email
        setIsDirty?.(!currentEmail)
    }, [currentEmail, setIsDirty]);

    const submitCode = useCallback(async ({ code }: VerificationCodeFormValues) => {
        try {
            await authUserService.VerifyEmailUpdate({ email: emailToVerify!, code });
            toast.success("Your Contact Email has been updated!");
            setEmailToVerify(undefined);
            setIsDirty?.(false);
            afterSubmit?.();
        } catch (e) {
            captureException(e);
            if (e instanceof Error) {
                toast.error(e.message || e.name);
            }
            throw e;
        }
    }, [afterSubmit, emailToVerify, setIsDirty]);

    return <div className={classNames(className)}>
        {!isUpdateForm
            ? <div className='flex flex-row gap-1'>
                <p className='select-none truncate text-base flex-1'>{currentEmail}</p>
                <button
                    className='text-primary text-primary-700 font-semibold'
                    onClick={() => setIsUpdateForm(true)}
                >Edit</button>
            </div>
            : <>
                <UpdateEmailForm
                    onCancel={currentEmail ? () => setIsUpdateForm(false) : undefined}
                    onSubmit={async ({ email }) => {
                        try {
                            await authUserService.EmailGenerateAndSend(email);
                            setEmailToVerify(email);
                        } catch (e) {
                            captureException(e);
                            toast.error("Update failed. Please check this is a valid email.");
                        }
                    }}
                    setIsDirty={setIsDirty}
                    isRequired={!currentEmail}
                />
            </>
        }
        <Modal size='sm' hideX disableBackdropClickable onClose={() => setEmailToVerify(undefined)} open={!!emailToVerify}>
            <VerificationCodeForm
                email={emailToVerify!}
                onCancel={() => setEmailToVerify(undefined)}
                onSubmit={submitCode}
            />
        </Modal>
    </div>
}